import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HOURS, MINUTES } from 'src/app/core/constants/time.constants';
import { deviceEnvironmentId } from 'src/app/core/enums/device-environment-id';
import { PaymentHistoryRequest } from 'src/app/core/models/request/payment-history.request';
import { CloverService } from 'src/app/core/services/clover.service';
import { NativeBridgeService } from 'src/app/core/services/native-bridge.service';
import { PaymentsService } from 'src/app/core/services/payments.service';
import { PrintLotClosureService } from 'src/app/core/services/print-lot-closure.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { dateFormatNotHour } from 'src/app/shared/functions/date.functions';
import { DatePipe } from '@angular/common';
import html2pdf from 'html2pdf.js';
import { CierreLoteService } from 'src/app/core/services/cierre-lote.service';
import { ResumenOperacionesRequest } from 'src/app/core/models/request/resumen-operaciones.request';

@Component({
  selector: 'app-cierre-lote-popup',
  templateUrl: './cierre-lote-popup.component.html',
  styleUrls: ['./cierre-lote-popup.component.scss']
})
export class CierreLotePopupComponent implements OnInit {
  @ViewChild('pdfContent') pdfContent: ElementRef;

  form: FormGroup;
  hours = HOURS;
  minutes = MINUTES;
  isLoading = false;
  resumentResponse = false;
  resumen = null;
  environment = null;
  terminalId = null;
  buttonLabel = 'Descargar';
  totalMovements = 0;
  totalAmount = 0;
  maxDate: Date = new Date();
  minDate: Date;
  currentDate = new Date();
  currentHour = this.currentDate.getHours().toString().padStart(2, '0');
  availableHours = this.hours;
  printing = false;

  constructor(
    private fb: FormBuilder,
    private storage: StorageService,
    private nativeBridgeService: NativeBridgeService,
    private printLotClosureService: PrintLotClosureService,
    private cloverService: CloverService,
    private datePipe: DatePipe,
    private cierreLoteService: CierreLoteService,
    private cdr: ChangeDetectorRef
  ) {
    this.minDate = this.calculateMinDate();
  }

  ngOnInit(): void {
    this.environment = this.nativeBridgeService.getEnvironment();
    if (this.environment == deviceEnvironmentId.CLOVER) {
      this.terminalId = this.cloverService.getTerminalId();
      this.terminalId ? (this.buttonLabel = 'Imprimir') : null;
    }
    this.createForm();
    this.updateAvailableHours(this.currentDate);
    this.form.get('dateFrom')?.valueChanges.subscribe((date) => {
      this.updateAvailableHours(date);
    });
  }

  calculateMinDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date;
  }

  createForm() {
    this.form = this.fb.group(
      {
        dateFrom: [new Date()],
        hourFrom: ['00'],
        minutesFrom: ['00'],
        dateTo: [],
        hourTo: [this.currentHour],
        minutesTo: ['59']
      },
      {}
    );
    if (this.terminalId) {
      this.form.addControl('terminal', this.fb.control('1', Validators.required));
    }
    this.initializeForm();
  }

  initializeForm() {
    this.form.get('dateFrom')?.valueChanges.subscribe((date) => {
      this.updateAvailableHours(date);
      const validHours = this.availableHours.map((h) => h.value);
      const hourFromControl = this.form.get('hourFrom');
      const hourToControl = this.form.get('hourTo');
      hourFromControl.setValue(validHours[0]);

      if (this.isToday(date)) {
        const nowHour = new Date().getHours().toString().padStart(2, '0');
        hourToControl.setValue(nowHour);
      } else {
        hourToControl.setValue(validHours[validHours.length - 1]);
      }

      this.cdr.detectChanges();
    });
  }

  async generarResumen() {
    this.isLoading = true;
    let request = this._createRequest();
    await this._cargarMovimientos(request);
  }

  updateAvailableHours(selectedDate: Date) {
    if (this.isToday(selectedDate)) {
      const nowHour = new Date().getHours();
      this.availableHours = this.hours.filter((hour) => parseInt(hour.value, 10) <= nowHour);
    } else {
      this.availableHours = [...this.hours];
    }
  }

  isToday(date: Date): boolean {
    const d = date instanceof Date ? date : new Date(date);
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()
    );
  }

  private _createRequest(): ResumenOperacionesRequest {
    let filterTerminalId = null;
    if (this.terminalId && this.form.get('terminal')?.value == '1') {
      filterTerminalId = this.terminalId;
    }
    let request: ResumenOperacionesRequest = {
      idTerminal: filterTerminalId,
      fechaDesde: dateFormatNotHour(this.form.get('dateFrom').value),
      horaDesde: this.form.get('hourFrom').value + ':' + this.form.get('minutesFrom').value,
      fechaHasta: dateFormatNotHour(this.form.get('dateFrom').value),
      horaHasta: this.form.get('hourTo').value + ':' + this.form.get('minutesTo').value
    };
    return request;
  }

  async _cargarMovimientos(request) {
    const responseResumenOperaciones = await this.cierreLoteService.getResumenOperacionesPix(request).catch((Error) => {
      this.isLoading = false;
    });
    this.totalMovements = responseResumenOperaciones ? responseResumenOperaciones.object?.sizePix : null;
    this.totalAmount = responseResumenOperaciones ? responseResumenOperaciones.object?.totalPix : null;
    this._generarResumen();
    responseResumenOperaciones ? (this.resumentResponse = true) : null;
    this.isLoading = false;
  }

  private _generarResumen() {
    const formatoPesosArgentinos =
      this.totalAmount?.toLocaleString('es-AR', {
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) || null;
    let allTerminals = null;
    if (this.form?.get('terminal')) {
      this.form?.get('terminal')?.value == '1' ? (allTerminals = false) : (allTerminals = true);
    }

    this.getCurrentDate();

    this.resumen = {
      desde:
        dateFormatNotHour(this.form.get('dateFrom').value) +
        ' - ' +
        this.form.get('hourFrom').value +
        ':' +
        this.form.get('minutesFrom').value,
      hasta:
        dateFormatNotHour(this.form.get('dateFrom').value) +
        ' - ' +
        this.form.get('hourTo').value +
        ':' +
        this.form.get('minutesTo').value,
      totalRecaudado: 'ARS $' + formatoPesosArgentinos,
      totalMovimientosPix: this.totalMovements,
      user:
        JSON.parse(this.storage.get('user')).persona.apellido +
        ' ' +
        JSON.parse(this.storage.get('user')).persona.nombres,
      terminalId: this.terminalId,
      currentDate: this.getCurrentDate(),
      currentDateTime: this.getCurrentTime(),
      allTerminals: allTerminals
    };
  }

  imprimirDescargarResumen() {
    this.printing = true;
    setTimeout(() => {
      this.printing = false;
    }, 3000);

    if (this.terminalId) {
      this.printLotClosureService.sendLotClosureToDevice(this.terminalId, this.resumen);
    } else {
      this.downloadQR();
    }
  }

  downloadQR() {
    const options = {
      filename: 'resumen-operaciones.pdf',
      image: { type: 'png', quality: 0.98 },
      imageDpi: 500,
      html2canvas: { scale: 1, logging: true },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        margins: { top: 10, bottom: 10, left: 10, right: 10 }
      }
    };

    html2pdf().from(this.pdfContent.nativeElement).set(options).save();
  }

  backToForm() {
    this.resumentResponse = false;
  }

  getCurrentDate() {
    const currentDate = new Date();
    const today = currentDate.getDate();
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    return `${today}/${month}/${year}`;
  }

  getCurrentTime() {
    const currentDate = new Date();
    const offset = -3; // Desired GMT offset
    const currentDateTime = this.datePipe.transform(currentDate, 'HH:mm:ss', `GMT${offset}`);
    return currentDateTime;
  }

  getCurrentTimeForPdf() {
    const currentDate = new Date();
    const offset = -3; // Desired GMT offset
    const currentDateTime = this.datePipe.transform(currentDate, 'HH:mm', `GMT${offset}`);
    return currentDateTime;
  }
}
